import { Select, Form } from "formik-antd";
import { useEffect, useMemo } from "react";
import { ReportColumn } from "/app/src/models";
import { useTranslation } from "react-i18next";
import { Col } from "antd";
import { useFormikContext } from "formik";

interface FormValues {
  tableColumn: [string, number] | undefined;
  name: string | undefined;
  reportId?: number;
  firstColumn: number | undefined;
  secondColumn: number | undefined;
  filteringValue: string | undefined;
}

/**
 * Comparison component that renders columns and filtering options.
 * @param {Object} props - The component props.
 * @param {ReportColumn[]} props.columns - Array of report columns.
 * @returns {JSX.Element} The rendered Comparison component.
 * Comparison component that renders columns and filtering options.
 * @param {Object} props - The component props.
 * @param {ReportColumn[]} props.columns - Array of report columns.
 * @returns {JSX.Element} The rendered Comparison component.
 */
export default function Comparison({ columns }: { columns: ReportColumn[] }) {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<FormValues>();

  /**
   * Memoized values for available columns and column type.
   * It filters the columns based on the selected first and second columns.
   * It also determines the type of the columns (DateTime or Number).
   * If no columns are selected, it defaults to showing all columns except String type.
   */
  const { availableColumns, columnType } = useMemo(() => {
    const currentDay = {
      id: 0,
      name: "Current Day",
      filterType: "DateTime",
    };

    /**
     * Resolves the column based on the given column ID.
     * @param {number|undefined} colId The ID of the column to resolve.
     * @returns The matching column object if found, otherwise the current day object for ID 0 or undefined.
     */
    const resolveColumn = (colId: number | undefined) => {
      if (colId === 0) return currentDay;
      return columns.find((col) => col.id === colId);
    };

    const firstCol = resolveColumn(values.firstColumn);
    const secondCol = resolveColumn(values.secondColumn);

    const activeCol = firstCol || secondCol;

    if (activeCol) {
      const columnType = activeCol.filterType as "DateTime" | "Number";
      const matchingColumns = columns.filter(
        (col) => col.filterType === columnType,
      );
      const availableColumns =
        columnType === "DateTime"
          ? [...matchingColumns, currentDay]
          : matchingColumns;

      return { availableColumns, columnType };
    } else {
      const availableColumns = [
        ...columns.filter((col) => col.filterType !== "String"),
        currentDay,
      ];
      const columnType = undefined;

      return { availableColumns, columnType };
    }
  }, [columns, values.firstColumn, values.secondColumn]);

  const numericFilteringValues = [
    { value: "percentage", label: t("translation:percentage") },
    { value: "ratio", label: t("translation:ratio") },
    { value: "sum", label: t("translation:sum") },
    { value: "product", label: t("translation:product") },
    { value: "modulus", label: t("translation:modulus") },
    { value: "difference", label: t("translation:difference") },
  ];

  const dateTimeFilteringValues = [
    { value: "minute", label: t("translation:minute") },
    { value: "hour", label: t("translation:hour") },
    { value: "day", label: t("translation:day") },
    { value: "week", label: t("translation:week") },
    { value: "month", label: t("translation:month") },
    { value: "year", label: t("translation:year") },
  ];

  useEffect(() => {
    if (columnType === undefined) {
      setFieldValue("filteringValue", undefined);
    }
  }, [columnType, setFieldValue]);

  return (
    <>
      <Col span={4}>
        <Form.Item name="firstColumn">
          <Select
            allowClear
            name="firstColumn"
            size="large"
            placeholder={t("translation:select_column")}
          >
            {availableColumns
              .filter((column) => column.id !== values.secondColumn)
              .map((column) => (
                <Select.Option key={column.id} value={column.id}>
                  {column.name}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>
      </Col>
      {columnType !== "DateTime" && (
        <Col span={3}>
          <Form.Item name="filteringValue">
            <Select
              name="filteringValue"
              size="large"
              placeholder={t("translation:select_operation")}
              disabled={columnType === undefined}
            >
              {numericFilteringValues.map((filter) => (
                <Select.Option key={filter.value} value={filter.value}>
                  {filter.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      )}
      <Col span={4}>
        <Form.Item name="secondColumn">
          <Select
            allowClear
            name="secondColumn"
            size="large"
            placeholder={t("translation:select_column")}
          >
            {availableColumns
              .filter((column) => column.id !== values.firstColumn)
              .map((column) => (
                <Select.Option key={column.id} value={column.id}>
                  {column.name}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>
      </Col>
      {columnType === "DateTime" && (
        <Col span={3}>
          <Form.Item name="filteringValue">
            <Select
              name="filteringValue"
              size="large"
              placeholder={t("translation:unit_of_time")}
            >
              {dateTimeFilteringValues.map((filter) => (
                <Select.Option key={filter.value} value={filter.value}>
                  {filter.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      )}
    </>
  );
}
